import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Modal from '@components/common/Modal'
import ToggleSwitch from '@components/common/ToggleSwitch'
import UsDateInput from '@components/common/UsDateInput'
import reportViewsService from '@api/v1/reportViewsService'
import { useResultMaxAgeDays } from '@hooks/useResultMaxAgeDays'
import { ReportFilterFields } from '../ReportFilterPanel'
import RecurrenceEditor from './RecurrenceEditor'
import SegmentedRadio from './SegmentedRadio'
import EmailRecipientsField from './EmailRecipientsField'
import useOwnDeliveryAddress from './useOwnDeliveryAddress'
import { recipientsFormatSwitch, ownerNeedsAddress, ownerAddressError, normalizeOwnerAddress } from './emailRecipients'
import { defaultRecurrence } from './recurrenceDefaults'
import { fmtTime, fmtWhenInZone } from './queueFormat'
import {
  reportDateSummaries, reportDatePreset, reportDatePresetChoices, datedColumnCount, wireFilterCount,
} from './rollForwardPreset'
import { InfoIcon } from './queueIcons'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'
import { PRIVATE_SCHEDULE_HELP, PRIVATE_SCOPE_OPTION_LABEL } from './jobVisibility'
import { adHocRetentionLabel, scheduleRetentionHelp } from './resultRetention'

/**
 * "Run this report" — the queue / schedule modal (spec 120).
 *
 * ## What changed and why
 *
 * This was a two-pane split: the report's own filter editor on the left, timing
 * and delivery on the right. Himaad reviewed it 2026-09-08 and kept the
 * behaviour but rejected the shape — *"I feel like the newer structure, it feels
 * more consistent with the rest of the system and these kind of modals that we
 * have in payments and other like new UI refresh areas, especially for a flow
 * where people need to also understand timing, recurrence and visibility."*
 *
 * So it is now ONE narrow column of stacked sections separated by rules, in the
 * mock's order: what it will run with → when → the mode's own controls →
 * delivery. Same three modes, same wire, same guards.
 *
 * The mock was transcribed from two screenshots (the Figma seat quota is out and
 * Dev Mode is unapproved on this account), so every radius, border and colour
 * here is a PROJECT TOKEN rather than a hex read off a scaled PNG. Nothing in
 * this file claims pixel fidelity — see `specs/120-run-modal-mock-rebuild.md`
 * for the provenance note and the full list of deviations.
 *
 * ## The three things in here that are correctness, not styling
 *
 * 1. **The filter editor cannot be deleted on a picker host.** The mock replaces
 *    it with a read-only summary card plus `Edit filters`, and it can do that
 *    because it is drawn over a REPORT PAGE, which has a filter bar to send you
 *    back to. `ReportQueue` and `ReportSchedules` open this modal with no report
 *    in context and no filter bar anywhere: `ReportFilterFields` below is their
 *    ONLY scoping control. Delete it there and the schedule is created with
 *    whatever `buildInitialFilters` happened to seed — `[]` for a report with no
 *    `filterDefault`s, or `$$cb_all` (the WIDEST possible scope) for the WIP
 *    family. CB's Newtonsoft deserialization ignores absent filter keys, so the
 *    stored proc then runs UNSCOPED, the run reports SUCCESS, and a firm-wide
 *    WIP/AR figure is emailed on a cadence. So the card is ADDITIVE on those
 *    hosts and the editor stays.
 *
 * 2. **`rollForward` is derived from the report, never defaulted.** See §7 of the
 *    spec and `rollForwardPreset.js`. It used to be a hardcoded `'lastMonth'`.
 *
 * 3. **`Ends` is not rendered.** The mock draws `Ends [ Never ]`; there is no
 *    such field — no end/until/count column in any migration and no termination
 *    concept in `recurrence.js`. A select whose only option is "Never" would be
 *    a promise nothing keeps, and persisting a value nothing reads is worse.
 *    Filed as its own follow-up in the spec.
 *
 * ## The filter area never asserts an absence it can't substantiate
 *
 * "This report has no filters." is a factual claim, and for most of the catalog
 * it would be a false one. The branch order below is load-bearing and unchanged
 * — `columnsError` is tested BEFORE `columns.length === 0`, because a failed
 * read also yields an empty array:
 *
 *   loading      — the definition read is in flight
 *   unavailable  — the read FAILED (`columnsError`), or the report is one whose
 *                  filters this modal cannot reach (`unsupportedReason`)
 *   none         — the read succeeded and the report genuinely has no filters
 *
 * This is the empty-vs-broken trap that has cost this project four tickets
 * (CC-49595, CC-50153, the schedules page, the 200-with-empty-array endpoint).
 * The summary card inherits the obligation: it renders ONLY in the success arm,
 * so a filter count or a date window can never appear over a read that failed.
 */

/** A stacked section with the mock's full-width rule above it. */
const Section = ({ children, first }) => (
  <div className={first ? 'px-5 py-4' : 'border-t border-rc-line px-5 py-4'}>{children}</div>
)

/** The mock's small bold section label. #111827 on white is 17.7:1. */
const SectionLabel = ({ id, children }) => (
  <span id={id} className="mb-1.5 block text-[11px] font-semibold text-rc-ink">{children}</span>
)

/** "Tuesday 18 August at 6:00 AM" — the once-mode footer's computed moment. */
const fmtOnceMoment = (ms) => {
  if (ms == null) return null
  try {
    const day = new Date(ms).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })
    return `${day} at ${fmtTime(ms)}`
  } catch {
    return null
  }
}

/*
 * spec 120 §6.1: the mock drops the "Keep results" control. The FIELD stays at
 * its DB default (`retain_count INTEGER DEFAULT 5`, and cb-api clamps
 * `Number(body.retainCount) || 5`), so dropping the control changes no stored
 * value — an omitted `retainCount` would land on the same 5. It is still sent
 * explicitly so the payload says what it means rather than relying on two
 * defaults agreeing.
 */
const RETAIN_COUNT_DEFAULT = 5

const QueueReportModal = ({
  report, columns = [], values = {}, onChange, accountIds, onClose,
  onQueueNow, onQueueLater, onSchedule, isSubmitting,
  // The wire filter array a schedule created from here would store. Optional:
  // hosts that do not have one (the Queue page picks a report but never builds
  // its wire filters) simply omit it, and the server then makes no claim about
  // whether roll-forward applies rather than guessing. See CC-50232 F1.
  scheduleFilters,
  reportOptions, selectedReportId, onSelectReport, loadingColumns,
  // The definition read failed. Distinct from "returned nothing" — see the
  // header comment. Pass `onRetryColumns` alongside so the user has a way out.
  columnsError, onRetryColumns,
  // This report's filters exist but are not reachable from THIS modal, and
  // queueing it from here would produce a job that cannot run.
  // `{ message, to, linkLabel }` — `to` routes the user where it does work.
  unsupported,
  initialWhen = 'now',
  /*
   * Is there a filter bar BEHIND this modal? It decides whether the mock's
   * read-only card replaces the inline editor (§8 of the spec) — see point 1 in
   * the header for what happens if that is wrong.
   *
   * Defaulted by INFERENCE rather than asserted, and the inference is exact
   * today: `reportOptions` is supplied only by `useQueueReportPicker`, i.e. only
   * by the two hosts that have no report in context and therefore no filter bar
   * (`ReportQueue.jsx`, `ReportSchedules.jsx`). `ReportView.jsx` and
   * `SpecializedFilterBar.jsx` both render `ReportFilterPanel` on the page and
   * pass no `reportOptions`. It is still a PROXY for the property that matters,
   * so it is an overridable prop: a future host that breaks the correlation
   * passes the truth instead of inheriting a guess.
   */
  filtersEditableBehind,
  /*
   * Can a schedule created from THIS host actually store a roll-forward token?
   *
   * Not a style flag — it is the difference between a control and a lie. The
   * token only ever reaches CB down the GENERIC path: cb-api stamps it onto the
   * stored filter array (`schedules.js:227`, gated on `kind === 'generic'`) and
   * the runner re-resolves it at fire time only for that kind
   * (`runner.js:643`). A specialized or accounting schedule stores an endpoint
   * REPLAY SPEC instead, which has no filter array to stamp — and
   * `SpecializedFilterBar.jsx` therefore, deliberately, sends no `rollForward`
   * at all.
   *
   * Left alone, the toggle rendered there anyway and read ON by default, because
   * those pages' synthesized date field IS spelled `filterTypeName: 'DATE'` and
   * its All-Dates value reverse-matches the `all` preset. So the modal promised
   * "each run reports on All Dates as of the day it fires" on 21 report pages
   * where nothing stores the promise — and the CC-50232 warning that exists to
   * catch exactly that cannot fire there either, because the warning needs
   * `filters`, which those hosts do not pass. Two silences, one screen.
   *
   * Defaults TRUE because three of the four hosts are generic. The specialized
   * bar passes false. Roll-forward for those pages needs the per-page
   * date-field map recorded on CC-50148; until then the honest UI is no toggle.
   */
  rollForwardSupported = true,
  /*
   * Spec 192: with `rollForwardSupported` false, the sentence saying what a recurring
   * run's dates will be — when the host knows it is not "they stay where you put them".
   * Omitted everywhere but the Exago page, which can ask its run route for the run's own day.
   */
  scheduleDatesNote,
  /*
   * Can a run queued from THIS host be emailed with the export ATTACHED?
   *
   * Spec 137 (*review*): cb-api attaches only for a GENERIC job (`mailer.js` — a
   * specialized or accounting job's params are a replay spec that CB's
   * `DownloadReport` cannot read, so the export failed on every attempt anyway). On
   * any other host "Excel file attached" was a promise the server is now designed
   * never to keep, every time. Same shape as `rollForwardSupported`: the specialized
   * bar passes false, and the attach options are not offered there.
   */
  attachSupported = true,
  /*
   * Spec 194 — which files the OWNER's own copy may carry. Separate from
   * `attachSupported`, which decides other recipients. A generic host gets both formats,
   * as before; a specialized page passes the formats its own server export can produce,
   * so cb-api's mailer can attach exactly that export (`jobs/specializedExport.js`).
   * Empty = link only. Spec 200: the specialized bar now passes `attachSupported` true
   * wherever these are non-empty — other recipients get that same file — and the first
   * address moves Link to one of THESE formats, never to one the report lacks.
   */
  ownAttachFormats = attachSupported ? ['xlsx', 'csv'] : [],
}) => {
  const canAttachXlsx = ownAttachFormats.includes('xlsx')
  const canAttachCsv = ownAttachFormats.includes('csv')
  // Spec 152 — the results bucket's age, for the retention sentence below (null when no
  // schedules list has declared it: then the sentence states no period at all).
  const resultMaxAgeDays = useResultMaxAgeDays()
  const [when, setWhen] = useState(initialWhen)
  /*
   * S4-D9 — the one-time run's moment is a DATE plus a TIME, not one
   * `datetime-local`, and the reason is not cosmetic.
   *
   * A native `datetime-local` renders in the browser/OS locale, so on a non-US
   * machine it showed DD/MM/YYYY while every other date on the page — the filter
   * pills, the criteria summary, the schedule preview — showed MM/DD/YYYY. On a
   * financial product that is a date the user can misread by eleven months, and
   * V1 forces US format, so the two apps disagreed too.
   *
   * `UsDateInput` (CC-49824) already solved exactly this for report filters: US
   * text with a native picker behind a button, emitting ISO. Reused rather than
   * re-solved. The wire format is unchanged — ISO in, epoch ms out.
   */
  const [runDate, setRunDate] = useState('')
  const [runTime, setRunTime] = useState('09:00')
  // Validity is resolved in the CHANGE handlers, not during render: reading
  // Date.now() while rendering is impure (react-hooks/purity) and makes the
  // button's enabled state depend on when React happened to re-render.
  const [runAtMs, setRunAtMs] = useState(null)
  const [recurrence, setRecurrence] = useState(defaultRecurrence)
  const [scheduleName, setScheduleName] = useState('')
  // Delivery (spec 059): self-only email, link or an attached export. PDF is not
  // emailable (no server-side report PDF), so it's not offered here.
  const [deliverEmail, setDeliverEmail] = useState(false)
  const [deliverFormat, setDeliverFormat] = useState('link')
  /*
   * Spec 138 — a SCHEDULE's other recipients. Offered only under Repeating, only with
   * email on, and only where the export can be attached (a generic host): an ad-hoc run
   * stays self-only, and a link only opens for the owner. `formatSwitchNote` is the
   * visible sentence said when adding the first address moves Link to Excel — the
   * format is never changed silently.
   */
  const [recipients, setRecipients] = useState([])
  const [formatSwitchNote, setFormatSwitchNote] = useState(null)
  /*
   * Spec 139 — the address an owner whose account has NONE supplies for the schedule:
   * their own copy goes there, and Centerbase needs it to send to anyone. Asked for only
   * where the field above is offered, and only when the access token is KNOWN to carry
   * no usable address. `ownerAddressAttempted` counts the Creates refused for it — each
   * one shows the inline problem again (*review*: a count, so a second refusal is news
   * to the field); the ref is where focus goes then.
   */
  const [ownerAddress, setOwnerAddress] = useState('')
  const [ownerAddressAttempted, setOwnerAddressAttempted] = useState(0)
  const ownerAddressRef = useRef(null)
  /*
   * Visibility, for a SCHEDULE only — see the long note at the control.
   * `groups` is `null` until loaded and `groupsFailed` is separate from an empty
   * array, because "we couldn't ask" and "you belong to none" are different
   * answers (CC-49595).
   */
  const [scope, setScope] = useState('mine')
  const [groupId, setGroupId] = useState('')
  const [groups, setGroups] = useState(null)
  const [groupsFailed, setGroupsFailed] = useState(false)
  // The server-computed preview, lifted so the footer can name the FIRST RUN
  // from the same engine the cron uses. Null whenever nothing is computed.
  const [schedulePreview, setSchedulePreview] = useState(null)

  /*
   * Groups load lazily, only once the user actually picks the group scope, and
   * from the SAME endpoint saved-view sharing uses: it returns only groups you
   * BELONG to, because sharing is restricted to those. `/security/groups` would
   * offer options that can only ever 403 — 7 of vladdemo's 10 groups have no
   * members (CC-49595).
   */
  useEffect(() => {
    if (when !== 'repeat' || scope !== 'group' || groups !== null || groupsFailed) return undefined
    let alive = true
    reportViewsService.groups()
      .then((g) => { if (alive) setGroups(g || []) })
      .catch(() => { if (alive) setGroupsFailed(true) })
    return () => { alive = false }
  }, [when, scope, groups, groupsFailed])

  /*
   * §4 — the mode control. The mock draws TWO segments; the code has THREE
   * modes, and the third one is not optional: `when === 'now'` is the only path
   * in the whole app to an immediate background run, reached from the Queue
   * page's "Queue a report" button, from `JobEmptyState`, and from both report
   * pages' "Run in the background now" menu item. Deleting the segment while
   * `initialWhen='now'` is still passed would also leave `when` holding a value
   * no segment represents — nothing selected on screen, and `submit()` still
   * calling `onQueueNow`.
   *
   * `ariaLabel` keeps each option's ORIGINAL accessible name while the visible
   * label shortens to the mock's word. That is better for AT (the long form says
   * what the mode does) and it keeps five locators in files this stream does not
   * own working across a purely visual change.
   */
  const whenOptions = [
    { key: 'now', label: 'Now', ariaLabel: 'Right now, in the background' },
    { key: 'once', label: 'Once', ariaLabel: 'Once, at a specific date & time' },
    { key: 'repeat', label: 'Repeating', ariaLabel: 'On a repeating schedule' },
  ]

  const whenHelp = when === 'now'
    ? 'Starts immediately. You can close this and keep working — it lands in your queue.'
    : when === 'once'
      ? 'Useful for a heavy report you want sitting there first thing in the morning.'
      : 'Creates a saved schedule. It shows up under Recurring schedules where you can pause or edit it.'

  /*
   * Both halves resolve through one function, so neither can be validated against
   * a stale copy of the other. The composed value is still parsed as LOCAL time
   * (`YYYY-MM-DDTHH:mm`, no trailing Z) — exactly what `datetime-local` produced,
   * so the wire behaviour is byte-identical to before.
   */
  const resolveRunAt = (dateIso, time) => {
    if (!dateIso || !time) { setRunAtMs(null); return }
    const ms = new Date(`${dateIso}T${time}`).getTime()
    setRunAtMs(Number.isFinite(ms) && ms > Date.now() ? ms : null)
  }
  const onRunDateChange = (v) => { setRunDate(v); resolveRunAt(v, runTime) }
  const onRunTimeChange = (v) => { setRunTime(v); resolveRunAt(runDate, v) }

  /*
   * §7 — roll-forward.
   *
   * The token is DERIVED from this report's own date filter, never defaulted.
   * `datePreset` is null when there is no resolvable preset (a custom range, or
   * two date filters that disagree); the control is then hidden and the emitted
   * token is `'pinned'`, which is the only honest answer — a rolling window we
   * cannot name is a window we cannot promise.
   *
   * `datePresetChoices` is the third case, and the one the first pass got wrong:
   * several presets resolve to the report's window TODAY and to different
   * windows later. The derivation refuses to rank them (`rollForwardPreset.js`
   * refusal 3) and the user is asked instead, so nothing is guessed and the
   * capability is not lost for the 175 days a year on which a tie exists.
   *
   * `rollForwardChoice` is the USER's answer; `rollForward` is what goes on the
   * wire. Keeping them separate is what makes the emitted token testable and
   * stops a module default from ever reaching cb-api again.
   */
  /*
   * Memoised on `[columns, values]` — the same dependency `ReportFilterPanel`'s
   * own copy of this derivation uses.
   *
   * Being straight about what that buys, since a comment claiming more would be
   * the sort of thing this file exists to avoid: the reverse match resolves
   * EVERY preset as of `dayjs()`, so it reads the clock, and a `useMemo` reduces
   * how OFTEN that happens without moving it out of render. It does not make the
   * derivation pure. On `ReportView` the `columns` prop is an inline `.filter()`
   * — a new array identity every render — so the memo re-runs anyway. It is
   * tolerable where `resolveRunAt`'s clock read was not, because nothing here
   * gates a control's enabled state on the result: the worst a stale read can do
   * is name a preset for one paint, and the emitted token is recomputed on the
   * render that submits.
   */
  const { datePreset, datePresetChoices, dateSummaries, wireCount, datedCount } = useMemo(() => ({
    datePreset: reportDatePreset(columns, values),
    // The tied presets when the value is one several of them produce today.
    // Never non-null alongside `datePreset` — see `reportDatePresetChoices`.
    datePresetChoices: reportDatePresetChoices(columns, values),
    // EVERY valued date window, not the first. The card names all of them, which
    // is what makes the residual count below true rather than merely arithmetic.
    dateSummaries: reportDateSummaries(columns, values),
    // Both counts come from the wire, never from `columns.length` — see
    // `wireFilterCount`. A count in this card is a claim about what narrows the
    // figures, so it counts filters that exist, not filters that could.
    wireCount: wireFilterCount(columns, values),
    datedCount: datedColumnCount(columns, values),
  }), [columns, values])
  /*
   * Every dated column is NAMED above, so subtracting all of them is now
   * correct. It was not before: the card named `dateSummaries[0]` alone and
   * still subtracted `datedCount`, so a second valued date filter was neither
   * named nor counted — it vanished, and the `· N other filters` clause that
   * would have hinted at it was suppressed at exactly 0.
   */
  const otherFilterCount = Math.max(0, wireCount - datedCount)

  /*
   * `null` = the user has not spoken; the default below applies. A token = they
   * have. Two states, not a boolean, because there are now three shapes of
   * answer — one preset (roll or pin), several tied presets (name one or pin),
   * and none (pin).
   */
  const [rollForwardChoice, setRollForwardChoice] = useState(null)
  /*
   * The preset the CONTROL may offer, which is not the same thing as the preset
   * the card may NAME. The card is describing the filters as they stand, which
   * is true on every host; the control is promising future behaviour, which is
   * only true where the token can be stored. See `rollForwardSupported`.
   */
  const rollForwardablePreset = rollForwardSupported ? datePreset : null
  const rollForwardableChoices = rollForwardSupported ? datePresetChoices : null
  /*
   * Rolling is the default where the window is unambiguous, because a pinned
   * recurring schedule re-reports one frozen window for ever while looking
   * healthy. Where the presets TIE it is not the default: choosing one of a tie
   * on the user's behalf is the blocker this fixes, so the honest default is the
   * visibly-wrong one and the user is asked.
   */
  const defaultRollForward = rollForwardablePreset ? rollForwardablePreset.key : 'pinned'
  /*
   * A choice is only honoured while it is still one of the answers this report
   * can give. Without this, editing the filters behind an open modal leaves a
   * token selected for the OLD window on the wire — the same class of silent
   * substitution as the aliasing itself, arriving by a different door.
   */
  const rollForwardOffered = new Set([
    'pinned',
    ...(rollForwardablePreset ? [rollForwardablePreset.key] : []),
    ...(rollForwardableChoices ? rollForwardableChoices.options.map((o) => o.key) : []),
  ])
  const rollForward = rollForwardChoice && rollForwardOffered.has(rollForwardChoice)
    ? rollForwardChoice
    : defaultRollForward

  const runAtValid = runAtMs != null
  // Only reachable under Repeat, where the control exists at all.
  const scopeIncomplete = when === 'repeat' && scope === 'group' && !groupId
  // "Incomplete" and "in the past" are different problems and get different copy:
  // a half-filled pair is not an error the user has made yet.
  const runAtStarted = !!(runDate || runTime)
  const canSubmit = !isSubmitting
    && !(reportOptions && !selectedReportId)
    // Never let a run be booked that we already know cannot execute, and never
    // book one whose filters we failed to load: an unfiltered run of a
    // financial report is not a smaller answer, it is a different one.
    && !unsupported
    && !columnsError
    && (when !== 'once' || runAtValid)
    // "A group…" with no group picked would be sent as scope='group' with a null
    // groupId, which cb-api refuses (`validateScopeForWrite`) — so refuse it here
    // rather than let the user meet a 400 after filling the whole form.
    && !scopeIncomplete

  /*
   * Spec 138 — the "Also send to" field is offered only where its addresses can really
   * be emailed: a REPEATING schedule (ad-hoc runs stay self-only), with email on, on a
   * host whose report can be attached. Elsewhere nothing about other recipients is sent.
   */
  const recipientsOffered = when === 'repeat' && deliverEmail && attachSupported
  const owner = useOwnDeliveryAddress(recipientsOffered)
  const hasRecipients = recipientsOffered && recipients.length > 0
  // Spec 139 §2.7: the "Your email address" input is showing, so a Create needs it.
  const needsOwnerAddress = recipientsOffered && ownerNeedsAddress(owner)
  // …and the hint must not say the copy goes to "the address you sign in with": there is none.
  const ownCopyGoesTo = needsOwnerAddress ? 'the address you enter below' : 'the address you sign in with'
  /*
   * Link is never left selected while other addresses will be sent (§3.7, *review*).
   *
   * The addresses are kept while the field is hidden (email set to Nobody, or When moved
   * off Repeating), so the Link option is enabled again there — and choosing it, or
   * moving back to Repeating with it chosen, brought the list back with Link selected:
   * the disabled option showing as the value, a note claiming Excel, and a Create that
   * cb-api refused. Every change that can bring the list back with Link goes through
   * here, so the first-address rule holds on every path, and says so.
   */
  const settleFormat = (nextWhen, nextEmail, nextFormat, list = recipients) => {
    if (nextWhen === 'repeat' && nextEmail && attachSupported && list.length > 0 && nextFormat === 'link') {
      // Spec 200: to a format this report really has (GL Budget: CSV only).
      const to = recipientsFormatSwitch(ownAttachFormats)
      setDeliverFormat(to.format)
      setFormatSwitchNote(to.note)
      return
    }
    setDeliverFormat(nextFormat)
  }
  const onRecipientsChange = (next) => {
    // A link only opens for the owner, so the first address moves Link to Excel — and
    // says so, visibly. It is never switched silently (§3.7).
    if (next.length > 0 && recipients.length === 0) settleFormat(when, deliverEmail, deliverFormat, next)
    if (next.length === 0) setFormatSwitchNote(null)
    setRecipients(next)
  }
  const onWhenChange = (nextWhen) => {
    setWhen(nextWhen)
    if (deliverEmail) settleFormat(nextWhen, true, deliverFormat)
  }

  const submit = () => {
    const delivery = { deliverEmail, deliverFormat: deliverEmail ? deliverFormat : 'link' }
    if (when === 'now') return onQueueNow?.(delivery)
    if (when === 'once') return onQueueLater?.(runAtMs, delivery)
    // Spec 139 §2.7 — blocked inline, with the server's own sentence, rather than sent
    // to be refused: email is on and the owner has no address, so nothing could be sent.
    // The server refuses it too (§2.3); this only says so while the person is here.
    if (needsOwnerAddress && ownerAddressError(ownerAddress)) {
      setOwnerAddressAttempted((n) => n + 1)
      ownerAddressRef.current?.focus()
      return undefined
    }
    return onSchedule?.({
      name: scheduleName,
      recurrence,
      rollForward,
      retainCount: RETAIN_COUNT_DEFAULT,
      ...delivery,
      // Spec 138: a generic host sends the list ([] when email is off, so nothing is
      // stored); a host that cannot attach sends nothing about recipients at all.
      // Spec 139: and the owner's own address, only while it is being asked for — never
      // from the specialized host, and never for an account whose token has one.
      ...(attachSupported ? {
        deliverRecipients: recipientsOffered ? recipients : [],
        ...(needsOwnerAddress ? { deliverOwnerAddress: normalizeOwnerAddress(ownerAddress) } : {}),
      } : {}),
      // The server VALIDATES these against CB group membership and defaults a
      // caller that says nothing to 'mine', so sending them is safe and saying
      // nothing is also safe — but a schedule the user marked shared and that
      // silently saved as private would be the worse failure.
      scope,
      groupId: scope === 'group' ? groupId : null,
      groupName: scope === 'group'
        ? ((groups || []).find((g) => String(g.id) === String(groupId))?.name || null)
        : null,
    })
  }

  const submitLabel = isSubmitting
    ? (when === 'repeat' ? 'Creating…' : 'Queueing…')
    : when === 'repeat' ? 'Create schedule' : when === 'once' ? 'Schedule it' : 'Queue it'

  /*
   * §5 / §6 — the footer summary is COMPUTED, never a static sentence.
   *
   * The repeat branch reads the SERVER's own description and first run out of
   * the `/preview` response, which is the same engine the cron uses. It is not
   * derived locally: a second description of the same rule agrees today and
   * drifts later, and a first-run date the cron disagrees with is exactly the
   * class of claim this subsystem refuses to make. With no preview computed
   * there is no cadence sentence — the honest fallback, not a plausible one.
   */
  const onceMoment = when === 'once' ? fmtOnceMoment(runAtMs) : null
  const repeatDescription = schedulePreview?.description
    ? schedulePreview.description.charAt(0).toLowerCase() + schedulePreview.description.slice(1)
    : null

  const footerNote = when === 'now'
    ? <><b>Runs now in the background.</b> You can close this and keep working — it lands in your queue.</>
    : when === 'once'
      ? (onceMoment
        ? <><b>Runs once</b> on {onceMoment}, then comes off the schedule.</>
        : <><b>Runs once</b>, then comes off the schedule.</>)
      : (repeatDescription
        ? (
          <>
            <b>Runs {repeatDescription}.</b>{' '}
            First run {fmtWhenInZone(schedulePreview.firstRunAt, schedulePreview.tz)}.
          </>
        )
        : <><b>Runs on its own from now on.</b> Manage or pause it from Recurring schedules.</>)

  // See the prop comment: an inference with an override, not an assumption.
  const filtersBehind = filtersEditableBehind ?? !reportOptions

  /*
   * §3 — "It will run with".
   *
   * Rendered ONLY from the success arm of the filter branch below, so a count or
   * a date window can never sit over a failed or unfinished definition read.
   *
   * Two clauses the mock shows are deliberately NOT rendered, because no host
   * can supply them and §3 says to omit an unknown clause rather than invent it:
   *   - `About 1,100 rows` and `last run took 3.1s` are facts about a PRIOR run.
   *     There is no per-report last-run endpoint; the only source is the job
   *     list, which is scoped to the reader and windowed, so "last run" there
   *     means something narrower than the sentence claims.
   *   - `at their defaults` needs a diff against `buildInitialFilters`, which
   *     the modal is not given — and on the two report-page hosts `values` are
   *     the user's LIVE filters, which usually are NOT the defaults. So the
   *     count is rendered without that claim.
   */
  /*
   * Border is --color-rc-seam, not --color-rc-control, and that is a declared
   * deviation from §9 of the mock spec rather than a slip. This box has a
   * tinted fill AND sits on page white, so its edge has a different background
   * on each side — the case `src/index.css:85` names, and the one where
   * substituting --color-rc-control shipped 2.780:1 once already (spec 092 E4).
   * Recomputed from the tokens in `src/index.css`:
   *   --color-rc-control #8b95a5   3.03:1 on #ffffff   2.90:1 on #f9fafb
   *   --color-rc-seam    #6b7280   4.83:1 on #ffffff   4.63:1 on #f9fafb
   * Only the second clears 3.0:1 on both sides, and this card contains an
   * interactive link, so both sides is the bar. The link's own text is
   * --color-cb-accent-text #064891, 8.57:1 on #f9fafb, and underlined.
   */
  const runWithCard = (
    <div className="rounded-rc-card border border-rc-seam bg-gray-50 px-3 py-2.5">
      <span className="block text-[10.5px] text-rc-muted">It will run with</span>
      <p className="mt-0.5 text-[12.5px] leading-relaxed text-rc-ink">
        {dateSummaries.length === 1 ? (
          <>
            <b>{datePreset ? `${datePreset.label}, ${dateSummaries[0].summary}` : dateSummaries[0].summary}</b>
            {otherFilterCount > 0 && ` · ${otherFilterCount} other filter${otherFilterCount === 1 ? '' : 's'}`}
          </>
        ) : dateSummaries.length > 1 ? (
          /*
           * Two or more valued date filters, each NAMED with the column it
           * constrains. Naming the first and dropping the rest was the defect:
           * the run was scoped to July on `Paid Date` as well as August on
           * `Generated Date`, and the card said August. The column label is
           * carried here and not in the single-window case because with one
           * window there is nothing to tell apart, and the mock's sentence for
           * that case is what ships unchanged.
           *
           * `datePreset` is not consulted: with several windows it is either
           * null (they disagree) or the same preset for all of them, and
           * repeating one label per window reads as a claim that they differ.
           */
          <>
            {dateSummaries.map((d, i) => (
              <span key={d.columnId}>
                {i > 0 && ' · '}
                {d.label ? `${d.label} ` : ''}<b>{d.summary}</b>
              </span>
            ))}
            {otherFilterCount > 0 && ` · ${otherFilterCount} other filter${otherFilterCount === 1 ? '' : 's'}`}
          </>
        ) : wireCount > 0 ? (
          `${wireCount} filter${wireCount === 1 ? '' : 's'}, as set on the report`
        ) : (
          /*
            * Deliberately says nothing about SCOPE. The tempting sentence here
            * is "nothing narrows the figures", and on the generic path it would
            * be true — no filter keys means the stored proc runs unscoped. But
            * this card renders on the 21 specialized pages too, where some
            * fields carry server-side defaults that never appear in `values`,
            * so the stronger sentence would be a scope claim this component
            * cannot substantiate on every host. It reports what it holds.
            */
          'No filters set.'
        )}
      </p>
      {filtersBehind && (
        <div className="mt-1">
          {/*
            * Closing IS the navigation: this modal's only opener on a report-page
            * host lives inside `ReportFilterPanel`, and `Modal` restores focus to
            * whatever was focused when it opened — so dismissing puts the caret
            * back in the filter bar rather than at the top of the document.
            *
            * The visible words duplicate `ReportView.jsx:1660`'s collapsed-filter
            * button. They can never be in the DOM together (that button renders
            * only while the panel is collapsed, and collapsing unmounts this
            * modal's only opener), but a peer adding `getByText(/Edit filters/)`
            * would have no way to know that — hence the testid.
            */}
          <button
            type="button"
            data-testid="run-modal-edit-filters"
            onClick={onClose}
            className="text-[12px] font-semibold text-cb-accent-text underline"
          >
            Edit filters <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </div>
  )

  const filterEditor = (
    <div className="mt-3 flex flex-col gap-3">
      <ReportFilterFields
        columns={columns}
        values={values}
        onChange={onChange}
        accountIds={accountIds}
        fieldClassName="w-full"
      />
    </div>
  )

  return (
    <Modal
      title="Run this report"
      // Spec 216: a firm-authored report name (an Advanced Report's) is kept out of Pendo.
      subtitle={report
        ? (report.firmAuthored
          ? <span className={PENDO_IGNORE}>{`${report.name}${report.id ? ` · #${report.id}` : ''}`}</span>
          : `${report.name}${report.id ? ` · #${report.id}` : ''}`)
        : undefined}
      onClose={onClose}
      maxWidth="max-w-md"
      footer={(
        <>
          <span className="flex-1 text-[12px] leading-snug text-cb-navy">{footerNote}</span>
          <button type="button" onClick={onClose} className="btn-cb-toolbar">Cancel</button>
          <button type="button" onClick={submit} disabled={!canSubmit} className="btn-cb-primary">
            {submitLabel}
          </button>
        </>
      )}
    >
      {/* §3 + §8 — what this run will be scoped to. */}
      <Section first>
        {/* Report picker — present only when the modal is opened without a
            report already in context (from the Queue page rather than from a
            report). Same modal, one extra field. */}
        {reportOptions && (
          <div className="mb-3">
            <label htmlFor="queue-report-picker" className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
              Report
            </label>
            {/* `.select-rc` is unlayered, so it would defeat any h-/text-/px-/
                py-/rounded-/border- utility placed beside it — only `w-full`
                survives, and `.select-rc` deliberately sets no width. */}
            <select
              id="queue-report-picker"
              value={selectedReportId || ''}
              onChange={(e) => onSelectReport?.(e.target.value)}
              className="w-full select-rc"
            >
              <option value="">Choose a report…</option>
              {reportOptions.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        )}

        {reportOptions && !selectedReportId ? (
          <p className="text-[12.5px] text-gray-500">Choose a report to see its filters.</p>
        ) : loadingColumns ? (
          <p className="text-[12.5px] text-gray-500">Loading filters…</p>
        ) : unsupported ? (
          /* The report HAS filters — they just don't live on the generic
             definition this modal reads. Say where they do live and send the
             user there, rather than claiming an absence. */
          <div className="rounded-rc-card border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-900">
            <p>{unsupported.message}</p>
            {unsupported.to && (
              <Link to={unsupported.to} onClick={onClose} className="mt-1.5 inline-block font-semibold text-cb-accent-text underline">
                {unsupported.linkLabel || 'Open the report'}
              </Link>
            )}
          </div>
        ) : columnsError ? (
          /* A FAILED read. Never rendered as "no filters" — that turns an
             error into a false factual claim, and the run booked underneath
             it would silently be unscoped. And never rendered as the summary
             card either: a confident "This month · 3 other filters" over a read
             that failed is the same defect wearing the new design. */
          <div className="rounded-rc-card border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-900">
            <p>Couldn&apos;t load this report&apos;s filters, so we can&apos;t tell what it should be scoped to.</p>
            {onRetryColumns && (
              <button
                type="button"
                onClick={onRetryColumns}
                className="mt-1.5 font-semibold text-cb-accent-text underline"
              >
                Try again
              </button>
            )}
          </div>
        ) : columns.length === 0 ? (
          <p className="text-[12.5px] text-gray-500">This report has no filters.</p>
        ) : filtersBehind ? (
          runWithCard
        ) : (
          /* No filter bar behind this modal: the card SUMMARISES and the editor
             STAYS. See point 1 in the header — this is the one deletion that
             books an unscoped financial run. */
          <>
            {runWithCard}
            {filterEditor}
          </>
        )}
      </Section>

      {/* §4 — When. */}
      <Section>
        <SectionLabel id="queue-when-label">When</SectionLabel>
        <SegmentedRadio
          name="queue-when"
          labelledBy="queue-when-label"
          value={when}
          onChange={onWhenChange}
          options={whenOptions}
        />
        <p className="mt-1.5 text-[11.5px] text-rc-muted">{whenHelp}</p>
      </Section>

      {/* §5 — ONE-TIME future run: just a moment. The date range stays exactly
          as picked — the user chose a window AND a moment, so rolling it forward
          would quietly change what they asked for. */}
      {when === 'once' && (
        <Section>
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[9rem] flex-1">
              <label htmlFor="queue-run-date" className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
                Date
              </label>
              {/*
                * Built from plain utilities, with NO `.input-rc`, and that is not a
                * style preference. `src/index.css` is unlayered, so `.input-rc`'s
                * `padding: 0 0.75rem` beats the `pr-7` that `UsDateInput` appends
                * for its calendar-button clearance — the text would run under the
                * icon. `pl-2.5` rather than `px-2.5` for the same reason: a
                * `padding-inline` shorthand and a `padding-right` longhand both
                * target this edge, and which one wins depends on emission order
                * rather than on anything readable here.
                */}
              <UsDateInput
                id="queue-run-date"
                value={runDate}
                onChange={onRunDateChange}
                className="w-full rounded-rc-control border border-rc-control py-1.5 pl-2.5 text-[12.5px] text-rc-ink focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-cb-navy"
              />
            </div>
            <div className="min-w-[7rem]">
              <label htmlFor="queue-run-time" className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
                Time
              </label>
              {/*
                * Still a native `type="time"`. Unlike a date, a time input has
                * no ambiguity to fix — 06:00 cannot be misread as another
                * moment the way 08/18 can be read as 18 August or a nonexistent
                * month — and the browser's own 12/24-hour rendering follows the
                * user's OS preference, which is the right authority for a clock.
                */}
              <input
                id="queue-run-time"
                type="time"
                value={runTime}
                onChange={(e) => onRunTimeChange(e.target.value)}
                className="w-full rounded-rc-control border border-rc-control px-2.5 py-1.5 text-[12.5px] text-rc-ink focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-cb-navy"
              />
            </div>
          </div>
          <p className="mt-1.5 text-[11.5px] text-rc-muted">
            {runAtStarted && !runAtValid
              ? <span className="text-amber-700">Pick a date and a time in the future.</span>
              : 'Your local time. The filters above are kept exactly as they are.'}
          </p>
          {/*
            * Where a one-time run actually LANDS, said plainly.
            *
            * The design puts Once and Repeating in one modal under one title,
            * and its Repeating helper says "It shows up under Recurring
            * schedules". A user who picks Once will reasonably look there and
            * find nothing: a one-time run is a JOB with a `scheduled_for`,
            * promoted by the cron — it is never a `schedules` row, and the
            * recurrence engine has no one-time frequency at all.
            */}
          <p className="mt-1 text-[11.5px] text-rc-muted">
            It lands in your <b>queue</b> when it runs — one-time runs don&apos;t appear
            under Recurring schedules.
          </p>
        </Section>
      )}

      {/* §6 — RECURRING. */}
      {when === 'repeat' && (
        <>
          <Section>
            <RecurrenceEditor
              value={recurrence}
              onChange={setRecurrence}
              rollForward={rollForward}
              onRollForwardChange={setRollForwardChoice}
              rollForwardPreset={rollForwardablePreset}
              rollForwardPresetChoices={rollForwardableChoices}
              rollForwardSupported={rollForwardSupported}
              fixedDatesNote={scheduleDatesNote}
              onPreviewChange={setSchedulePreview}
              filters={scheduleFilters}
              idPrefix="queue"
              variant="mock"
            />
          </Section>

          <Section>
            <label htmlFor="queue-schedule-name" className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
              Schedule name
            </label>
            <input
              id="queue-schedule-name"
              type="text"
              value={scheduleName}
              onChange={(e) => setScheduleName(e.target.value)}
              placeholder="e.g. Monday morning billable hours"
              maxLength={80}
              className="w-full input-rc"
            />
            {/*
              * The "Keep results" CONTROL is dropped per the mock (§6.1) and the
              * field stays at its DB default of 5. This sentence is not dropped
              * with it: scheduled results are pruned BY COUNT while ad-hoc runs
              * expire by TIME, the two retention models genuinely differ, and no
              * other surface in this modal says so.
              *
              * Spec 152: both halves now come from `resultRetention.js` and are
              * bounded by the results bucket's age — every kept run lasts at most
              * that long, and on prod (2 days) "Ad-hoc runs are kept for 1 week" was
              * five days too long.
              */}
            <p className="mt-1.5 text-[11.5px] text-rc-muted" data-testid="queue-schedule-retention">
              {scheduleRetentionHelp({ retainCount: RETAIN_COUNT_DEFAULT, maxAgeDays: resultMaxAgeDays })}
              {/* Spec 152 review: age unknown (no schedules list read, its read failed, or
                  it declared none) ⇒ no period at all. Falling back to the row's "1 week"
                  here promised prod's ad-hoc runs five days its bucket does not keep. */}
              {adHocRetentionLabel(resultMaxAgeDays) ? ` Ad-hoc runs are kept for ${adHocRetentionLabel(resultMaxAgeDays)}.` : ''}
            </p>
          </Section>
        </>
      )}

      {/*
        * Visibility — LIVE for a schedule, absent for a one-off run
        * (Jordan, 2026-09-03: "was this feature not already completed? Lets
        * enable it").
        *
        * It was completed, on one side of a fork nobody had drawn:
        *
        *   `schedules` has `scope` / `group_id` / `group_name` columns, cb-api
        *   VALIDATES a write against CB group membership
        *   (`validateScopeForWrite`), the list endpoint resolves the audience
        *   server-side, and the schedules page's EDIT modal has had a working
        *   selector since CC-50282. Only this CREATE modal was never wired, so
        *   a schedule could be shared the moment after it was made but not
        *   while making it.
        *
        *   `jobs` has NO scope column at all, and its `shareable` flag is a
        *   server-computed DATA-SECURITY property (spec 055: 1 only for CUSTOM
        *   generic + accounting P&L, where the figures are tenant-scoped rather
        *   than user-filtered), not a user choice. There is nothing to store a
        *   one-off run's audience in, and inventing a client-side one would be
        *   a sharing promise the backend cannot keep — about a financial result.
        *
        * So the control renders only under Repeat, and the one-off branch says
        * plainly that the run is the owner's.
        *
        * The mock labels this `Who can see it`. It ships as `Who can see this`,
        * which is a DELIBERATE deviation: that is the exact wording the edit
        * modal already uses for the same control, and one control should not have
        * two names in one product. The mock's variant also gains nothing and
        * would rewrite four live locators.
        *
        * `mine` / `group` / `firm` are the API's own vocabulary (`SCOPES` in
        * `cb-api/src/domains/reports/scope.js`). For a SCHEDULE, `firm` means
        * opt-in publish — `WHERE tenant=? AND (user_id=? OR scope='firm')` — not
        * the whole tenant, which is what it means for a job.
        */}
      <Section>
        {when === 'repeat' ? (
          <>
            <label htmlFor="queue-schedule-scope" className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
              Who can see this
            </label>
            <select
              id="queue-schedule-scope"
              value={scope}
              onChange={(e) => { setScope(e.target.value); setGroupId('') }}
              className="select-rc w-full"
            >
              {/* Spec 169: Centerbase administrators can see (and edit) a private schedule. */}
              <option value="mine">{PRIVATE_SCOPE_OPTION_LABEL}</option>
              <option value="group">A group…</option>
              <option value="firm">Everyone at the firm</option>
            </select>
            {scope === 'group' && (
              <>
                <select
                  aria-label="Group to share with"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                  className="select-rc mt-2 w-full"
                >
                  <option value="">
                    {groupsFailed
                      ? `Couldn't load your groups`
                      : groups === null ? 'Loading groups…' : groups.length === 0 ? 'No groups found' : 'Pick a group…'}
                  </option>
                  {(groups || []).map((g) => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
                </select>
                {/* A failed read is a fact about the network; an empty one is a
                    fact about you. Conflating them hid CC-49595 through two QA
                    rounds, so the two answers are never the same sentence. */}
                {groupsFailed && (
                  <p role="alert" className="mt-1.5 text-[11.5px] text-amber-900">
                    We couldn&apos;t reach the server, so this list is empty for a reason that
                    isn&apos;t you — it is not that you belong to no groups.{' '}
                    <button
                      type="button"
                      onClick={() => setGroupsFailed(false)}
                      className="font-semibold text-cb-accent-text underline"
                    >
                      Try again
                    </button>
                  </p>
                )}
              </>
            )}
            {/* Spec 169 — the edit modal's own words, from the same module. */}
            <p className="mt-1.5 text-[11.5px] text-rc-muted" data-testid="queue-scope-help">
              {scope === 'mine'
                ? PRIVATE_SCHEDULE_HELP
                : <>Sharing lets colleagues <b>see</b> this schedule and whether it&apos;s running. Only you can pause, edit, run or delete it — and only you can open its results.</>}
            </p>
          </>
        ) : (
          <p className="text-[11.5px] text-rc-muted">
            This run is yours — you stay the owner and only you can open the result. Sharing
            applies to a repeating <span className="font-semibold">schedule</span>, not to a single run.
          </p>
        )}
      </Section>

      {/* §5 / §6 — delivery. */}
      <Section>
        {/*
          * TWO facts, not one control wearing one label.
          *
          * The mock draws a single toggle, ON by default, whose helper reads "A
          * notification in the bell. It is always in your queue either way." —
          * that describes the queue-and-bell notification, which is
          * unconditional and therefore an inert ON switch, exactly as it is
          * today. The mock ALSO draws a separate `Email it to` field, and email
          * is the opt-in.
          *
          * Collapsing the two would break one of them either way: a single live
          * toggle makes a mandatory thing look optional, and a single inert
          * toggle silently removes the only way to ask for email. So the bell
          * stays inert and the email control stays live.
          *
          * `Email it to` is a FORMAT select, not a recipient input, and it cannot
          * become one: `deliverTo` is no longer an API input at all — delivery is
          * bound to the verified token at every write path and at send time
          * (CC-53046), and a foreign address is refused even with delivery off.
          * The options name the recipient ("Me — …") so the absence of a
          * recipient field reads as a fact rather than as a missing feature.
          * ⚠ Superseded for SCHEDULES by spec 138 (2026-10-07): under Repeating,
          * an "Also send to" field (`EmailRecipientsField`) adds up to 25 more (spec 201)
          * addresses below this select; `deliverTo` stays refused, ad-hoc runs self-only.
          * ⚠ And by spec 139 (2026-10-08): an owner whose token has NO address types one
          * there ("Your email address", `deliverOwnerAddress`); everyone else is unchanged.
          *
          * The empty option is the mock's grey placeholder, and it is a real
          * empty VALUE: choosing it sets `deliverEmail` false, which is what the
          * wire carries. PDF is not offered — cb-api refuses it outright
          * (CC-53100), because CB has no server-side report PDF.
          */}
        <SectionLabel>When it&apos;s ready</SectionLabel>
        <ToggleSwitch
          checked
          disabled
          label="Tell me when it lands"
          labelClassName="text-[12.3px] text-rc-ink-2"
        />
        <p className="mt-1 mb-3 ml-[44px] text-[11.5px] text-rc-muted">
          A notification in the bell. It is always in your queue either way.
        </p>
        <label htmlFor="queue-deliver-format" className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
          Email it to
        </label>
        <select
          id="queue-deliver-format"
          value={deliverEmail ? deliverFormat : ''}
          aria-describedby="queue-deliver-format-hint"
          onChange={(e) => {
            const next = e.target.value
            setDeliverEmail(!!next)
            // The person chose: an earlier switch note no longer describes the format.
            setFormatSwitchNote(null)
            if (next) settleFormat(when, true, next)
          }}
          className="w-full select-rc"
        >
          <option value="">Nobody, it lands in the queue only</option>
          {/* Spec 138: disabled — not removed — while other addresses are added, with
              the reason in the hint below: a link only opens for the owner. */}
          <option value="link" disabled={hasRecipients}>Me — a link to open it here</option>
          {canAttachXlsx && <option value="xlsx">Me — Excel file attached</option>}
          {canAttachCsv && <option value="csv">Me — CSV file attached</option>}
        </select>
        {/* Spec 137 §2.11: never "from you". With SendUsingUserMailSettings off,
            Centerbase chooses the sender, so all this may promise is where the email
            goes and whose mail sends it. */}
        <p id="queue-deliver-format-hint" className="mt-1.5 text-[11.5px] text-rc-muted">
          {!deliverEmail
            ? (when === 'repeat' && attachSupported
              ? 'Reports can only be emailed to the address you sign in with, plus any other addresses you add to a schedule.'
              : 'Reports can only be emailed to the address you sign in with.')
            : deliverFormat === 'link'
              ? `Goes to ${ownCopyGoesTo}, sent through Centerbase’s mail. You sign in to view it.${ownAttachFormats.length ? '' : " This report type can't be attached yet."}`
              : `Goes to ${ownCopyGoesTo}, sent through Centerbase’s mail. Large files are sent as a link instead.`}
          {hasRecipients && ' Link can’t be chosen while other addresses are added, because a link only opens for you — and a file too large to attach isn’t sent to them.'}
        </p>
        {/* *review* — the live region is ALWAYS mounted and only its content changes: a
            region inserted with its sentence already in it is not reliably announced, and
            the switch happens while focus is in the address input, not on the select. */}
        <div aria-live="polite" data-testid="format-switch-live">
          {hasRecipients && formatSwitchNote && (
            <p data-testid="format-switch-note" className="mt-1 text-[11.5px] font-semibold text-amber-900">
              {formatSwitchNote}
            </p>
          )}
        </div>
        {/* Spec 138 §3.7 — a schedule's other recipients; a sentence instead on a host
            whose report cannot be attached. Never on an ad-hoc run. */}
        {when === 'repeat' && deliverEmail && (
          <EmailRecipientsField
            idPrefix="queue-recipients"
            owner={owner}
            recipients={recipients}
            onChange={onRecipientsChange}
            supported={attachSupported}
            ownerAddress={ownerAddress}
            onOwnerAddressChange={setOwnerAddress}
            ownerAddressAttempted={ownerAddressAttempted}
            ownerAddressInputRef={ownerAddressRef}
          />
        )}
      </Section>

      {/*
        * The access disclosure, kept and gated on `when !== 'now'` exactly as
        * before. §6.1 asks for an explicit decision: it stays, because a
        * scheduled run fires later on a STORED credential, and that is a
        * security-relevant fact about a financial report rather than chrome. The
        * mock has no equivalent panel; that is the deviation, recorded.
        */}
      {when !== 'now' && (
        <Section>
          <div className="flex gap-2.5 rounded-rc-card border border-cb-accent/30 bg-cb-light px-3 py-2.5 text-[11.7px] leading-relaxed text-cb-navy">
            <InfoIcon className="mt-px w-3.5 h-3.5 shrink-0" />
            <span>
              Scheduled runs use your own access, so they see exactly the data you see. If you
              don&apos;t sign in for a long stretch the schedule pauses itself and tells you — it
              never runs as somebody else.
            </span>
          </div>
        </Section>
      )}
    </Modal>
  )
}

export default QueueReportModal
